/**
 * Avatar Color Utilities
 *
 * Provides deterministic color assignment for colored avatars based on name hashing. Colors are consistent — the same
 * name always produces the same color.
 *
 * @package @complexcare/harmony
 */

/** Color pair for avatar background, text, and border */
export interface AvatarColorPair {
  /** Background color (light pastel) */
  bg: string;
  /** Text color (darker shade for contrast) */
  text: string;
  /** Border color (mid-tone between bg and text) */
  border: string;
}

/**
 * Palette of 27 light pastel colors with accessible text contrast and matching borders. Designed to cover a wide range
 * of first-initial combinations (27×27 mapped via hash). Each pair meets WCAG AA contrast ratio for text against its
 * background.
 */
const AVATAR_COLOR_PALETTE: readonly AvatarColorPair[] = [
  { bg: '#FEF3C7', text: '#92400E', border: '#F6C547' }, // Amber
  { bg: '#DBEAFE', text: '#1E40AF', border: '#93B4ED' }, // Blue
  { bg: '#D1FAE5', text: '#065F46', border: '#6EE7A8' }, // Green
  { bg: '#EDE9FE', text: '#5B21B6', border: '#C4B5FD' }, // Violet
  { bg: '#FCE7F3', text: '#9D174D', border: '#F9A8D4' }, // Pink
  { bg: '#E0E7FF', text: '#3730A3', border: '#A5B4FC' }, // Indigo
  { bg: '#CCFBF1', text: '#115E59', border: '#5EEAD4' }, // Teal
  { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' }, // Red
  { bg: '#FEF9C3', text: '#854D0E', border: '#FDE047' }, // Yellow
  { bg: '#F3E8FF', text: '#6B21A8', border: '#D8B4FE' }, // Purple
  { bg: '#CFFAFE', text: '#155E75', border: '#67E8F9' }, // Cyan
  { bg: '#FFEDD5', text: '#9A3412', border: '#FDBA74' }, // Orange
  { bg: '#F4F9E9', text: '#4B6A2B', border: '#CDDEA9' }, // Sage (from Figma)
  { bg: '#E8F5E9', text: '#2E7D32', border: '#A5D6A7' }, // Mint
  { bg: '#FFF3E0', text: '#E65100', border: '#FFCC80' }, // Tangerine
  { bg: '#E3F2FD', text: '#1565C0', border: '#90CAF9' }, // Sky
  { bg: '#FCE4EC', text: '#C62828', border: '#F48FB1' }, // Rose
  { bg: '#F3E5F5', text: '#7B1FA2', border: '#CE93D8' }, // Orchid
  { bg: '#E0F2F1', text: '#00695C', border: '#80CBC4' }, // Seafoam
  { bg: '#FFF8E1', text: '#F57F17', border: '#FFE082' }, // Gold
  { bg: '#E8EAF6', text: '#283593', border: '#9FA8DA' }, // Periwinkle
  { bg: '#EFEBE9', text: '#4E342E', border: '#BCAAA4' }, // Taupe
  { bg: '#E1F5FE', text: '#0277BD', border: '#81D4FA' }, // Ice
  { bg: '#F1F8E9', text: '#33691E', border: '#AED581' }, // Lime
  { bg: '#FBE9E7', text: '#BF360C', border: '#FFAB91' }, // Coral
  { bg: '#E0F7FA', text: '#006064', border: '#80DEEA' }, // Aqua
  { bg: '#F9FBE7', text: '#827717', border: '#DCE775' }, // Chartreuse
] as const;

/**
 * Returns a deterministic color pair for a given name. Hash is based on the first character of the first name and first
 * character of the last name (27×27 = 729 possible combinations mapped to the palette). The same name always produces
 * the same color.
 *
 * @example
 *   ```ts
 *   const color = getAvatarColor('Peter Parker');
 *   // color.bg = '#F4F9E9'
 *   // color.text = '#4B6A2B'
 *   // color.border = '#CDDEA9'
 *   ```;
 *
 * @param name - Full name string (e.g., "Peter Parker") or single name (e.g., "Peter")
 * @returns Color pair with `bg` (background), `text`, and `border` properties
 */
export function getAvatarColor(name: string | undefined | null): AvatarColorPair {
  if (!name || name.trim().length === 0) {
    return AVATAR_COLOR_PALETTE[0]!;
  }

  const parts = name.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const firstChar = parts[0]?.charAt(0) ?? '';
  const lastChar = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';

  // Map chars: a=1..z=26, empty/other=0  →  27 × 27 = 729 combinations
  const charCode = (c: string): number => {
    if (c >= 'a' && c <= 'z') return c.charCodeAt(0) - 96; // a=1, z=26
    return 0;
  };

  const hash = charCode(firstChar) * 27 + charCode(lastChar);
  const index = hash % AVATAR_COLOR_PALETTE.length;
  return AVATAR_COLOR_PALETTE[index]!;
}

/**
 * Extracts initials from a full name string. Takes the first character of the first name and first character of the
 * last name. If only one name part exists, returns a single character.
 *
 * @param name - Full name string (e.g., "Peter Parker")
 * @returns Uppercase initials (e.g., "PP", "K")
 */
export function getNameInitials(name: string | undefined | null): string {
  if (!name || name.trim().length === 0) {
    return '';
  }

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    return (parts[0]!.charAt(0) || '').toUpperCase();
  }

  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}
